import config from 'appConfig';
import { CateringGA4Item, CateringOrderPayload } from 'lib/catering';

interface Item {
  itemId: string;
  itemName: string;
  coupon?: string;
  discount?: number;
  itemCategory?: string;
  itemVariant?: string;
  price?: number;
  quantity?: number;
}

type EcommerceEvent =
  | 'add_payment_info'
  | 'add_shipping_info'
  | 'add_to_cart'
  | 'add_to_wishlist'
  | 'begin_checkout'
  | 'earn_virtual_currency'
  | 'purchase'
  | 'refund'
  | 'remove_from_cart'
  | 'view_cart'
  | 'view_item'
  | 'view_item_list'
  | 'catering_addtocart'
  | 'catering_viewsummary'
  | 'catering_order_success'
  | 'reorder'
  | 'product_quick_view'
  | 'product_quick_custom';

interface SendEcommerceEvent {
  event: EcommerceEvent;
  data?: object;
  [key: string]: any;
}

interface EcommerceParams {
  currency?: string;
  value: number;
}

type WithSingleItem = EcommerceParams & {
  item: Item;
};

type WithMultipleItems = EcommerceParams & {
  items: Item[];
};

type PurchasingEcommerceParams = WithMultipleItems & {
  transactionId: string;
  shipping?: number;
  coupon?: string;
  tax?: number;
};

const mapToGTMEcommerceItem = (item: Item): object => {
  const {
    itemId,
    itemName,
    coupon,
    discount,
    itemCategory,
    itemVariant,
    price,
    quantity,
  } = item;

  return {
    item_id: itemId,
    item_name: itemName,
    coupon,
    discount,
    item_category: itemCategory,
    item_variant: itemVariant,
    price,
    quantity,
  };
};

const sendEcommerceEvent = ({
  event,
  data,
  ...rest
}: SendEcommerceEvent): void => {
  window.dataLayer = window.dataLayer || [];

  if (window.dataLayer) {
    /**
     * reference:
     * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm
     *
     * clear the previous ecommerce object
     */
    window.dataLayer.push({
      ecommerce: null,
      order_details: undefined,
      ...Object.fromEntries(
        Object.keys(rest).map((key) => [key, undefined]),
      ),
    });

    // push new ecommerce object
    window.dataLayer.push({
      event,
      ecommerce: data,
      ...rest,
    });
  }
};

export const addToCart = ({
  currency = config.currency,
  value,
  item,
}: WithSingleItem): void => {
  const data = {
    currency,
    value,
    items: [mapToGTMEcommerceItem(item)],
  };

  sendEcommerceEvent({ event: 'add_to_cart', data });
};

export const removeFromCart = ({
  currency = config.currency,
  value,
  item,
}: WithSingleItem): void => {
  const data = {
    currency,
    value,
    items: [mapToGTMEcommerceItem(item)],
  };

  sendEcommerceEvent({ event: 'remove_from_cart', data });
};

export const viewItem = ({
  currency = config.currency,
  value,
  item,
}: WithSingleItem): void => {
  const data = {
    currency,
    value,
    items: [mapToGTMEcommerceItem(item)],
  };

  sendEcommerceEvent({ event: 'view_item', data });
};

export const quickViewItem = ({
  currency = config.currency,
  value,
  item,
}: WithSingleItem): void => {
  const data = {
    currency,
    value,
    items: [mapToGTMEcommerceItem(item)],
  };

  sendEcommerceEvent({ event: 'product_quick_view', data });
};

export const customizeItem = ({
  currency = config.currency,
  value,
  item,
}: WithSingleItem): void => {
  const data = {
    currency,
    value,
    items: [mapToGTMEcommerceItem(item)],
  };

  sendEcommerceEvent({ event: 'product_quick_custom', data });
};

export const viewItems = ({
  itemListId,
  itemListName,
  items,
}: {
  itemListId?: string;
  itemListName?: string;
  items: Item[];
}) => {
  const _items = items.map((item) => mapToGTMEcommerceItem(item));

  const data = {
    item_list_id: itemListId,
    item_list_name: itemListName,
    items: _items,
  };

  sendEcommerceEvent({ event: 'view_item_list', data });
};

export const beginCheckout = ({
  currency = config.currency,
  value,
  items,
}: WithMultipleItems): void => {
  const _items = items.map((item) => mapToGTMEcommerceItem(item));

  const data = {
    currency,
    value,
    items: _items,
  };

  sendEcommerceEvent({ event: 'begin_checkout', data });
};

export const purchase = ({
  transactionId,
  currency = config.currency,
  shipping,
  coupon,
  value,
  tax,
  items,
}: PurchasingEcommerceParams): void => {
  const _items = items.map((item) => mapToGTMEcommerceItem(item));

  const data = {
    transaction_id: transactionId,
    value,
    tax,
    shipping,
    currency,
    coupon,
    items: _items,
  };

  sendEcommerceEvent({ event: 'purchase', data });
};

export const viewCart = ({
  currency = config.currency,
  value,
  items,
}: WithMultipleItems) => {
  const _items = items.map((item) => mapToGTMEcommerceItem(item));

  const data = {
    currency,
    value,
    items: _items,
  };

  sendEcommerceEvent({ event: 'view_cart', data });
};

export const cateringAddToCart = ({
  currency = config.currency,
  items,
  discount,
  coupon,
  value,
  raw,
}: {
  currency?: string;
  items: CateringGA4Item[];
  discount?: number;
  coupon?: string;
  value: number;
  raw?: CateringOrderPayload;
}) => {
  sendEcommerceEvent({
    event: 'catering_addtocart',
    data: {
      currency,
      items,
      discount,
      coupon,
      value,
    },
    order_details: raw,
  });
};

export const cateringViewSummary = ({
  currency = config.currency,
  items,
  discount,
  coupon,
  value,
  shipping,
  tax,
  raw,
}: {
  currency?: string;
  items: CateringGA4Item[];
  discount?: number;
  coupon?: string;
  value: number;
  shipping: number;
  tax: number;
  raw?: CateringOrderPayload;
}) => {
  sendEcommerceEvent({
    event: 'catering_viewsummary',
    data: {
      currency,
      items,
      discount,
      coupon,
      value,
      shipping,
      tax,
    },
    order_details: raw,
  });
};

export const cateringPurchase = ({
  currency = config.currency,
  transaction_id,
  items,
  discount,
  coupon,
  value,
  shipping,
  tax,
  raw,
}: {
  currency?: string;
  transaction_id: string;
  items: CateringGA4Item[];
  discount?: number;
  coupon?: string;
  value: number;
  shipping: number;
  tax: number;
  raw?: CateringOrderPayload;
}) => {
  sendEcommerceEvent({
    event: 'catering_order_success',
    data: {
      currency,
      transaction_id,
      items,
      discount,
      coupon,
      value,
      shipping,
      tax,
    },
    order_details: raw,
  });
};
